import { useState } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Body1,
  Caption1,
  Button,
  Input,
} from '@fluentui/react-components';
import {
  ShieldLockRegular,
  ArrowRepeatAllRegular,
  CopyRegular,
  CheckmarkRegular,
  OpenRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXL,
    width: '100%',
    alignItems: 'center',
    textAlign: 'center',
  },
  urlSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalS,
    width: '100%',
    maxWidth: '480px',
    alignItems: 'center',
  },
  urlRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    width: '100%',
  },
  permissions: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'flex-start',
    textAlign: 'left',
    maxWidth: '400px',
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
});

interface ConsentRequiredProps {
  consentUrl: string;
  onReset: () => void;
}

export function ConsentRequired({ consentUrl, onReset }: ConsentRequiredProps) {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(consentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const input = document.createElement('input');
      input.value = consentUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleApprove = () => {
    window.open(consentUrl, '_blank', 'noopener');
  };

  return (
    <div className={styles.container}>
      <ShieldLockRegular
        fontSize={48}
        color={tokens.colorPaletteMarigoldForeground1}
      />

      <Text size={600} weight="semibold">
        Admin Approval Required
      </Text>

      <Body1 style={{ color: tokens.colorNeutralForeground2, maxWidth: 480 }}>
        Your organisation's IT admin needs to grant read-only access to
        SharePoint so we can analyse your environment. This is a one-time
        approval that takes about 30 seconds.
      </Body1>

      <div className={styles.actions}>
        <Button
          appearance="primary"
          size="large"
          icon={<OpenRegular />}
          onClick={handleApprove}
        >
          Approve Access
        </Button>

        <Button
          appearance="subtle"
          icon={<ArrowRepeatAllRegular />}
          onClick={onReset}
        >
          Try Again
        </Button>
      </div>

      <div className={styles.urlSection}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          Not an admin? Share this link with your IT administrator:
        </Caption1>
        <div className={styles.urlRow}>
          <Input
            readOnly
            value={consentUrl}
            style={{ flex: 1 }}
            size="small"
          />
          <Button
            appearance="subtle"
            size="small"
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            onClick={handleCopy}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      </div>

      <div className={styles.permissions}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          Permissions being requested (read-only):
        </Caption1>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          &bull; <strong>User.Read</strong> &mdash; Sign in and read your profile
        </Caption1>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          &bull; <strong>Sites.Read.All</strong> &mdash; Read SharePoint site
          structure
        </Caption1>
        <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
          &bull; <strong>Files.Read.All</strong> &mdash; Read file names and
          metadata
        </Caption1>
      </div>
    </div>
  );
}
